import apiService from './apiService';
import type { AuditRecord } from '../components/common/AuditHistoryModal';

const BASE = '/api/v1/com/litigations';
const ORG  = '/api/v1/org';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface LitInstance {
  id: number;
  code?: string | null;
  entity_id: number;
  notice_id: string;
  notice_date: string | null;
  law_id: number | null;
  act: string | null;
  law_name: string | null;
  issued_by: string | null;
  section: string | null;
  due_date: string | null;
  amount: string | null;
  description: string | null;
  status: string;
  is_active: number;
  // workflow
  proceeding_id: number | null;
  proceeding_name: string | null;
  is_free_style: boolean;
  jurisdiction_id: number | null;
  jurisdiction_name: string | null;
  jurisdiction_text: string | null;
  start_date: string | null;
  assigned_position_id: number | null;
  assigned_position_name: string | null;
  assigned_user_id: number | null;
  assigned_user_name: string | null;
  current_step_id: number | null;
  current_step_label: string | null;
  next_due_date: string | null;
  created_by: number | null;
  created_on: string | null;
  // maker-checker workflow
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by?: number | null;
  submitted_on?: string | null;
  approved_by?: number | null;
  approved_on?: string | null;
  approval_note?: string | null;
  rejected_by?: number | null;
  rejected_on?: string | null;
  rejection_reason?: string | null;
}

export interface LitHistoryEntry {
  id: number;
  instance_id: number;
  seq_no: number;
  step_id: number | null;
  step_label: string;
  step_class_name: string | null;
  step_class_color: string | null;
  notes: string | null;
  next_due_date: string | null;
  recorded_by: number | null;
  recorded_by_name: string | null;
  recorded_on: string;
}

export interface LitMember {
  id: number;
  instance_id: number;
  position_id: number | null;
  user_id: number | null;
  position_name: string | null;
  user_name: string | null;
  added_by: number | null;
  added_on: string;
}

export interface PositionLookup {
  id: number;
  name: string;
  code: string | null;
  authority_name: string | null;
  location_name: string | null;
  user_id: number | null;
  occupant_name: string | null;
}

// ─── Instance CRUD ───────────────────────────────────────────────────────────

export const litInstanceService = {
  async getAll(): Promise<LitInstance[]> {
    const { data } = await apiService.get(`${BASE}`);
    return data.data;
  },

  async getMine(): Promise<LitInstance[]> {
    const { data } = await apiService.get(`${BASE}/mine`);
    return data.data;
  },

  async getById(id: number): Promise<LitInstance> {
    const { data } = await apiService.get(`${BASE}/${id}`);
    return data.data;
  },

  async create(payload: Partial<LitInstance>): Promise<LitInstance> {
    const { data } = await apiService.post(`${BASE}`, payload);
    return data.data;
  },

  async update(id: number, payload: Partial<LitInstance>): Promise<LitInstance> {
    const { data } = await apiService.put(`${BASE}/${id}`, payload);
    return data.data;
  },

  async close(id: number): Promise<void> {
    await apiService.post(`${BASE}/${id}/close`, {});
  },

  /** Flip active/inactive (1 ↔ 0). Returns the new is_active value. */
  async toggleStatus(id: number): Promise<number> {
    const { data } = await apiService.patch(`${BASE}/${id}/toggle`);
    return data?.data?.is_active ?? 1;
  },

  // ── Maker-checker workflow ──────────────────────────────────────────────
  async submit(id: number): Promise<void> {
    const { data } = await apiService.post(`${BASE}/${id}/submit`, {});
    if (data && data.success === false) throw new Error(data.error || data.detail || 'Submit failed');
  },

  async approve(id: number, note?: string): Promise<void> {
    const { data } = await apiService.post(`${BASE}/${id}/approve`, { note });
    if (data && data.success === false) throw new Error(data.error || data.detail || 'Approve failed');
  },

  async reject(id: number, reason: string): Promise<void> {
    const { data } = await apiService.post(`${BASE}/${id}/reject`, { reason });
    if (data && data.success === false) throw new Error(data.error || data.detail || 'Reject failed');
  },

  async reopen(id: number): Promise<void> {
    const { data } = await apiService.post(`${BASE}/${id}/reopen`, {});
    if (data && data.success === false) throw new Error(data.error || data.detail || 'Reopen failed');
  },

  /** Maker-checker audit trail for the workflow modal timeline. */
  async getWorkflowHistory(id: number): Promise<AuditRecord[]> {
    const { data } = await apiService.get(`${BASE}/${id}/workflow-history`);
    if (!data?.success || !data.data) return [];
    return (data.data as any[]).map((r: any) => ({
      audit_id: r.id,
      action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
      changed_by_username: r.changed_by_username || 'Unknown',
      changed_on: r.changed_on,
      change_reason: r.remarks,
    }));
  },
};

// ─── History ─────────────────────────────────────────────────────────────────

export const litHistoryService = {
  async getAll(instanceId: number): Promise<LitHistoryEntry[]> {
    const { data } = await apiService.get(`${BASE}/${instanceId}/history`);
    return data.data;
  },

  async advance(instanceId: number, payload: {
    step_id?: number | null;
    step_label: string;
    step_class_name?: string | null;
    step_class_color?: string | null;
    notes?: string;
    next_due_date?: string | null;
  }): Promise<void> {
    await apiService.post(`${BASE}/${instanceId}/advance`, payload);
  },
};

// ─── Team members ─────────────────────────────────────────────────────────────

export const litMembersService = {
  async getAll(instanceId: number): Promise<LitMember[]> {
    const { data } = await apiService.get(`${BASE}/${instanceId}/members`);
    return data.data;
  },

  async add(instanceId: number, payload: {
    position_id: number;
    user_id: number;
    position_name?: string;
    user_name?: string;
  }): Promise<LitMember> {
    const { data } = await apiService.post(`${BASE}/${instanceId}/members`, payload);
    return data.data;
  },

  async remove(instanceId: number, memberId: number): Promise<void> {
    await apiService.delete(`${BASE}/${instanceId}/members/${memberId}`);
  },
};

// ─── Org position lookup ──────────────────────────────────────────────────────

/** Row from GET /org/positions/pickable — every active position with its
 *  current occupant, for direct position assignment dropdowns. */
export interface PickablePosition {
  id: number;
  name: string;
  authority_id: number | null;
  location_id: number | null;
  authority_name: string | null;
  location_name: string | null;
  occupant_user_id: number | null;
  occupant_name: string | null;
}

export const litOrgService = {
  async lookupPosition(authorityId: number, locationId: number): Promise<PositionLookup> {
    const { data } = await apiService.get(
      `${ORG}/positions/lookup?authority_id=${authorityId}&location_id=${locationId}`
    );
    return data.data;
  },

  async getPickablePositions(): Promise<PickablePosition[]> {
    const { data } = await apiService.get(`${ORG}/positions/pickable`);
    return data.data ?? [];
  },

  // Approved + active only — the /dropdown endpoints filter server-side (entity
  // scoped, unbounded, sorted). The old paginated list leaked draft/unapproved
  // authorities & locations into these pickers.
  async getAuthorities(): Promise<{ id: number; name: string }[]> {
    const { data } = await apiService.get(`${ORG}/authorities/dropdown`);
    return data.items ?? [];
  },

  async getLocations(): Promise<{ id: number; name: string }[]> {
    const { data } = await apiService.get(`${ORG}/locations/dropdown`);
    return data.items ?? [];
  },
};
